import cx from 'classnames'
import React, { MouseEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useKey } from 'react-use'

import { useForwardRef } from 'hooks/useForwardRef'

import Styles from './Dropdown.module.scss'

export interface Props {
  children: ReactNode | (({ closeDropdown }: { closeDropdown: () => void }) => ReactNode)
  containerClassName?: string
  disabled?: boolean
  dropdownClassName?: string
  trigger: ReactNode | (({ opened }: { opened: boolean }) => ReactNode)
  stopOutsideClickPropagation?: boolean
  stopPropagation?: boolean
  triggerOn?: 'click' | 'hover'
  triggerActiveClassName?: string
  stayOpenOnContainerClick?: boolean
  closeTrigger?: (cb: Function) => void
  triggerCallback?: (isShown: boolean) => void
  containerActiveClassName?: string
  useClickCatcher?: boolean
  clickCatcherClassName?: string
  rightOffset?: number
  dismissOnEscape?: boolean
  /* Controlled mode */
  controlledShow?: boolean
}

export const Dropdown = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      containerClassName,
      dropdownClassName,
      triggerOn = 'hover',
      stopOutsideClickPropagation = true,
      stopPropagation = true,
      trigger,
      disabled,
      triggerActiveClassName = '',
      closeTrigger = () => {},
      triggerCallback = () => {},
      stayOpenOnContainerClick = false,
      containerActiveClassName = '',
      useClickCatcher = false,
      clickCatcherClassName,
      rightOffset,
      dismissOnEscape,
      controlledShow,
    }: Props,
    ref,
  ) => {
    const [show, setShow] = useState(false)
    const [dropUp, setDropUp] = useState(false)
    const triggerRef = useRef<HTMLDivElement>(null)
    const listRef = useForwardRef<HTMLDivElement>(ref)
    const containerRef = useRef<HTMLDivElement>(null)

    const effectiveShow = controlledShow ?? show

    const toggleShow = (event: MouseEvent<HTMLDivElement>) => {
      if (triggerOn !== 'click') {
        return
      }

      // In case we want to have several dropdown oppened at once
      stopPropagation && event.stopPropagation()
      setShow(!show)
    }

    useEffect(() => {
      triggerCallback?.(effectiveShow)
      closeTrigger(() => {
        return setShow
      })
    })

    useEffect(() => {
      if (effectiveShow && triggerRef.current && listRef.current) {
        const dropDownRect = triggerRef.current.getBoundingClientRect()
        const viewPortHeight = window.innerHeight
        const dropDownHeight = listRef.current?.offsetHeight
        const notEnoughSpaceDownwards = viewPortHeight - dropDownRect.bottom < dropDownHeight
        const elementSmallerThanViewPort = (dropDownHeight / viewPortHeight) * 100 < 90
        const openListUpwards = notEnoughSpaceDownwards && elementSmallerThanViewPort
        setDropUp(openListUpwards)
      }
    }, [effectiveShow, listRef])

    useEffect(() => {
      if (!effectiveShow || triggerOn === 'hover') return

      const hideDropdown = (event: Event) => {
        if (triggerRef.current?.contains(event.target as Node)) {
          return
        }

        if (stayOpenOnContainerClick && containerRef.current?.contains(event.target as Node)) {
          return
        }
        // NOTE: Tests started failing when I removed this stopPropagation and I
        // couldn't figure out why it would be necessary, so it's controllable by
        // a prop now
        stopOutsideClickPropagation && event.stopPropagation()

        setShow(false)
      }

      window.addEventListener('click', hideDropdown)

      return () => window.removeEventListener('click', hideDropdown)
    }, [effectiveShow, stayOpenOnContainerClick, stopOutsideClickPropagation, triggerOn])

    useLayoutEffect(() => {
      if (!effectiveShow || !listRef.current) return
      const listRect = listRef.current.getBoundingClientRect()
      if (listRect.left < 0) {
        listRef.current.style.right = `initial`
      }
    }, [effectiveShow, listRef])

    useKey('Escape', (e) => dismissOnEscape && setShow(false), { options: true }, [])

    if (disabled) {
      return (
        <div className={containerClassName}>
          {typeof trigger === 'function' ? trigger({ opened: !effectiveShow }) : trigger}
        </div>
      )
    }

    const dropdownContainer = (
      <div
        className={cx(Styles.container, containerClassName, { [containerActiveClassName]: effectiveShow })}
        ref={containerRef}
        {...(triggerOn === 'hover' ? { onMouseLeave: () => setShow(false) } : {})}
      >
        <div
          className={cx(Styles.trigger, {
            [Styles.hoverTrigger]: triggerOn === 'hover',
            [triggerActiveClassName]: effectiveShow,
          })}
          onClick={toggleShow}
          ref={triggerRef}
          data-testid="dropdownTriggerBtn"
          {...(triggerOn === 'hover' ? { onMouseOver: () => setShow(true) } : {})}
        >
          {typeof trigger === 'function' ? trigger({ opened: !effectiveShow }) : trigger}
        </div>
        <div
          ref={listRef}
          style={{ right: rightOffset }}
          className={cx(Styles.dropdown, dropdownClassName, {
            [Styles.showDropdown]: effectiveShow,
            [Styles.dropUp]: dropUp,
          })}
          data-testid="dropdownWrapper"
        >
          {typeof children === 'function' ? children({ closeDropdown: () => setShow(false) }) : children}
        </div>
      </div>
    )

    if (useClickCatcher) {
      return (
        <>
          {effectiveShow && <div className={clickCatcherClassName} onClick={() => setShow(false)}></div>}
          {dropdownContainer}
        </>
      )
    }

    return dropdownContainer
  },
)
